import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function BusinessOnboarding() {
    const [formData, setFormData] = useState({
        businessName: '',
        description: '',
        businessType: 'COMPANY', // Default assumption (update if Enum differs)
        countryCode: '', 
        website: ''
    });
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    useEffect(() => {
        const checkProfile = async () => {
            try {
                await axios.get(`${API_URL}/api/business/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                navigate('/business-dashboard');
            } catch (err) {
                setChecking(false);
            }
        };
        if (token) checkProfile();
    }, [token, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Force country code to uppercase (backend expects "US", not "us")
        const payload = {
            ...formData,
            countryCode: formData.countryCode.toUpperCase()
        };

        setLoading(true);

        try {
            await axios.post(`${API_URL}/api/business/profile`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Business profile created successfully!');
            navigate('/business-dashboard');
        } catch (err) {
            // If validation fails, show the exact error from the backend
            const errorMsg = err.response?.data?.message || 'Failed to create profile. Check your inputs.';
            toast.error(errorMsg);
            console.error("Backend Error Details:", err.response?.data);
        }
        setLoading(false);
    };

    if (checking) {
        return (
            <div className="auth-page">
                <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card" style={{ maxWidth: '600px', padding: '48px' }}>
                <div className="auth-brand fade-up delay-1" style={{ marginBottom: '32px' }}>
                    <h1 className="auth-logo">infloir</h1>
                    <p className="auth-tagline">Business Onboarding</p>
                </div>

                <div className="fade-up delay-2" style={{ marginBottom: '32px' }}>
                    <h2 className="auth-title" style={{ fontSize: '24px' }}>Set up your company profile</h2>
                    <p className="auth-subtitle">This information will be visible to workers on your campaigns.</p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* SECTION 1: COMPANY IDENTITY */}
                    <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                            Company Details
                        </h4>
                        
                        <div className="form-field fade-up delay-3">
                            <label className="form-label">Registered Business Name</label>
                            <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} placeholder="e.g. Acme Corporation" required />
                        </div>

                        <div className="form-field fade-up delay-3">
                            <label className="form-label">Business Type</label>
                            <select name="businessType" value={formData.businessType} onChange={handleChange} required>
                                <option value="E_COMMERCE">E-commerce / Retail</option>
                                <option value="SAAS">SaaS / Software</option>
                                <option value="AGENCY">Marketing Agency</option>
                                <option value="ENTERPRISE">Large Enterprise</option>
                                <option value="STARTUP">Startup</option>
                                <option value="OTHER">Other</option>
                            </select>
                        </div>

                        <div className="form-field fade-up delay-4">
                            <label className="form-label">Company Website</label>
                            <input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://acme.com" />
                        </div>

                        <div className="form-field fade-up delay-4">
                            <label className="form-label">Short Description</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Tell workers what your company does..." style={{ minHeight: '80px' }} />
                        </div>
                    </div>

                    {/* SECTION 2: LOCATION */}
                    <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                            Location
                        </h4>
                        <div className="form-field fade-up delay-5">
                            <label className="form-label">Country Code (2 Letters)</label>
                            <input 
                                type="text" 
                                name="countryCode" 
                                value={formData.countryCode} 
                                onChange={handleChange} 
                                placeholder="e.g. US, IN, GB" 
                                maxLength="2"
                                pattern="^[A-Z]{2}$"
                                title="Please enter a 2-letter country code (e.g., US, IN, GB)"
                                required 
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                Use standard 2-letter ISO codes (e.g., US for United States, IN for India).
                            </p>
                        </div>
                    </div>

                    <div className="fade-up delay-6" style={{ marginTop: '32px' }}>
                        <button type="submit" className="btn-primary auth-submit" disabled={loading} style={{ width: '100%' }}>
                            {loading && <span className="spinner" />}
                            {loading ? 'Creating Profile...' : 'Complete Setup'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default BusinessOnboarding;